import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { copyGalleryNotice } from '../domain/copy-gallery-notice'
import { UPLOAD_ITEM_STATUS, uploadItemKey } from '../domain/upload-queue'
import type { DocumentOwnerType } from '../models/document-owner'
import {
  type CopyGalleryMode,
  type DocumentVisibility,
  documentsAdminV2Service
} from '../services/documents-admin-v2.service'

/**
 * Datos del cartapacio de un dueño. Las tres operaciones invalidan la MISMA clave —la
 * del par `(ownerType, ownerId)`— y no el árbol entero: las cuatro pestañas coexisten
 * en la app y refrescar la de un proyecto no tiene por qué tirar la de una sociedad.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

type UseOwnerDocumentsArgs = {
  ownerType: DocumentOwnerType
  ownerId: string
  /** El listado exige el mismo permiso que escribir; sin él ni se pide. */
  enabled?: boolean
}

export function useOwnerDocumentsQuery({
  ownerType,
  ownerId,
  enabled = true
}: UseOwnerDocumentsArgs) {
  const accessToken = useV2AccessToken()

  return useQuery({
    queryKey: queryKeys.documents.byOwner(ownerType, ownerId),
    queryFn: async () => await documentsAdminV2Service.listByOwner(accessToken, ownerType, ownerId),
    enabled: enabled && Boolean(accessToken) && Boolean(ownerId),
    refetchOnWindowFocus: false,
    // Un 403 es determinista: reintentarlo sólo retrasa el aviso al usuario.
    retry: false
  })
}

/**
 * La plantilla global de categorías, que es el eje por el que la galería del cliente agrupa.
 *
 * **Filtra las deprecadas.** El endpoint las devuelve a propósito —el curador necesita verlas para
 * mover su contenido— pero ofrecerlas en un selector sería invitar a llenar una categoría que ya
 * se dio de baja. Se ordenan por `order`, que es el que fija la plantilla.
 */
export function useDocumentCategoriesQuery(enabled = true) {
  const accessToken = useV2AccessToken()

  return useQuery({
    queryKey: queryKeys.documents.categories(),
    queryFn: async () => await documentsAdminV2Service.listCategories(accessToken),
    select: (rows) => rows.filter((row) => !row.deprecatedAt).sort((a, b) => a.order - b.order),
    enabled: enabled && Boolean(accessToken),
    refetchOnWindowFocus: false,
    // Cambia cuando un administrador toca la plantilla, que es casi nunca.
    staleTime: 5 * 60 * 1000,
    retry: false
  })
}

/**
 * Las hojas firmadas de la MINIATURA generada de UN documento.
 *
 * ── POR QUÉ UNA CONSULTA POR DOCUMENTO ──────────────────────────────────────
 *
 * Porque cada firma vence sola. Una consulta que devolviera las cuarenta las ataría a un mismo
 * ciclo de caché: bastaría que una venciera para invalidar las treinta y nueve que todavía
 * servían, y agregar un documento tiraría el trabajo de las demás. De a una, cada miniatura se
 * renueva cuando le toca y React Query las pide en paralelo igual.
 *
 * ── `enabled` NO ES UNA OPTIMIZACIÓN ────────────────────────────────────────
 *
 * El listado ya dice, por fila, si hay hoja generada (`hasThumbnail`). Sin esa compuerta, un
 * cartapacio de cuarenta PDFs viejos —ninguno con derivados— dispararía cuarenta llamadas para
 * que las cuarenta contesten «no tiene». La tarjeta ya sabe qué dibujar antes de preguntar.
 *
 * ── EL `staleTime` NO ES UN NÚMERO A OJO ────────────────────────────────────
 *
 * Tiene que ser MENOR que la vigencia de la firma —quince minutos, los que declara el backend—
 * o la caché serviría una URL ya vencida: la imagen se rompe sola, sin que nada haya cambiado y
 * sin nada que reintentar, porque para React Query el dato está fresco. Diez deja cinco de
 * colchón para el viaje y para una pestaña que quedó abierta.
 */
export function useDocumentThumbnailQuery(attachmentId: string, enabled = true) {
  const accessToken = useV2AccessToken()

  return useQuery({
    queryKey: queryKeys.documents.thumbnailUrl(attachmentId),
    queryFn: async () => await documentsAdminV2Service.thumbnailUrl(accessToken, attachmentId),
    enabled: enabled && Boolean(accessToken) && Boolean(attachmentId),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Un 403 y un 404 son deterministas: reintentarlos gasta tres viajes para llegar a la misma
    // respuesta y demorar el arte de reemplazo, que es lo que la tarjeta va a dibujar igual.
    retry: false
  })
}

/**
 * Lo que este nivel HEREDA de sus ancestros: lo que el cliente ve y no se subió acá.
 *
 * No invalida con las mutaciones del dueño y eso es a propósito: publicar un documento PROPIO no
 * cambia lo que la sociedad publicó. Compartir clave con `byOwner` haría que cada publicación
 * refetcheara una lista que no se movió.
 *
 * Una sociedad no hereda de nadie, así que la consulta ni se dispara: el endpoint contestaría
 * una lista vacía y la pestaña ya sabe la respuesta sin preguntar.
 */
export function useInheritedDocumentsQuery({
  ownerType,
  ownerId,
  enabled = true
}: UseOwnerDocumentsArgs) {
  const accessToken = useV2AccessToken()

  return useQuery({
    queryKey: queryKeys.documents.inherited(ownerType, ownerId),
    queryFn: async () =>
      await documentsAdminV2Service.listInherited(accessToken, ownerType, ownerId),
    enabled: enabled && Boolean(accessToken) && Boolean(ownerId),
    refetchOnWindowFocus: false,
    retry: false
  })
}

/**
 * Abre el documento: pide la firma en el momento del click y devuelve la URL.
 *
 * **Se pide al abrir y no al listar.** La firma vive cinco minutos y la llamada AUDITA —escribe
 * un `DOWNLOAD` en la bitácora del adjunto—, así que pedirla para cuarenta filas que nadie abrió
 * llenaría de ruido justo el registro que sirve para saber quién se llevó un archivo.
 *
 * No lleva `useQuery` sino `useMutation` por lo mismo: esto no es un dato de la pantalla que se
 * pueda cachear y revalidar, es un ACTO con efecto registrado.
 */
export function useOpenDocumentMutation() {
  const accessToken = useV2AccessToken()

  return useMutation({
    mutationFn: async (id: string) => await documentsAdminV2Service.downloadUrl(accessToken, id),
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

/**
 * Renombra: cambia la ETIQUETA visible, no el archivo.
 *
 * ☠️ El endpoint existía desde la tajada del crédito y **sólo lo usaba Créditos**: en el
 * cartapacio, un `escaneo_final_v3(1).pdf` mal nombrado no se podía corregir, y la única salida
 * era borrarlo y volver a subirlo — que cuesta la bitácora del adjunto, su `assetId` y, si
 * estaba publicado, su fecha de publicación. La puerta estaba; faltaba el botón.
 *
 * Invalida TAMBIÉN lo heredado: el mismo documento puede estar arriba —como propio del nivel— y
 * abajo en el cartapacio de un hijo. Renombrarlo acá y dejar el nombre viejo allá sería mostrar
 * dos verdades del mismo archivo en la misma pantalla.
 */
export function useRenameDocumentMutation(ownerType: DocumentOwnerType, ownerId: string) {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) =>
      await documentsAdminV2Service.rename(accessToken, id, name),
    onSuccess: () => {
      toast.success('Nombre actualizado.')
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documents.byOwner(ownerType, ownerId)
      })
      void queryClient.invalidateQueries({ queryKey: ['documents', 'inherited'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export function useSetDocumentCategoryMutation(ownerType: DocumentOwnerType, ownerId: string) {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()

  return useMutation({
    mutationFn: async ({ id, categoryId }: { id: string; categoryId: string | null }) =>
      await documentsAdminV2Service.setCategory(accessToken, id, categoryId),
    onSuccess: (result) => {
      toast.success(
        result.categoryId
          ? 'Documento reclasificado.'
          : 'Documento sin categoría. Los clientes lo verán en «Otros documentos».'
      )
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documents.byOwner(ownerType, ownerId)
      })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

/** Lo que el diálogo de subida entrega: uno o varios archivos con sus metadatos comunes. */
export type UploadDocumentsInput = {
  files: File[]
  /** Nombre visible. Sólo se aplica cuando viene UN archivo — ver abajo. */
  name?: string
  comment?: string
  categoryId?: string
  /**
   * Publicar lo subido en el mismo acto.
   *
   * ⚠️ Encadena DOS actos, no los funde. La subida sigue naciendo interna —el backend no acepta
   * visibilidad al subir, y eso no cambió— y publicar sigue siendo una llamada aparte que el
   * servidor autoriza contra `PublishCatalogDocuments`. Lo único que se ahorra es el viaje a la
   * lista para apretar doce botones.
   *
   * Por eso el permiso NO queda decorativo, que es lo que el diseño original temía: quien no lo
   * tiene ni ve la casilla, y si la mandara igual recibiría un 403 por documento.
   */
  publish?: boolean
}

/**
 * Sube uno o varios archivos al cartapacio de un dueño, y opcionalmente los mete a un slot.
 *
 * ── POR QUÉ SECUENCIAL Y NO EN PARALELO ─────────────────────────────────────
 *
 * ☠️ Porque el ORDEN de la galería sale de acá. Entrar a un slot te da **la última posición**
 * —lo decide el backend, para que subir algo nunca reordene lo que otro acomodó—, así que la
 * posición final de cada foto es el orden en que sus llamadas TERMINAN. En paralelo eso es una
 * carrera: se eligen cinco fotos en un orden y la portada termina siendo la que pesaba menos.
 * De a una, el orden de selección es el orden de la galería, que es lo único que quien las
 * eligió puede predecir.
 *
 * Es más lento a propósito. Cinco archivos chicos es medio segundo, y a cambio el resultado es
 * el que se pidió en vez de el que salió.
 *
 * ── UN AVISO, NO UNO POR ARCHIVO ────────────────────────────────────────────
 *
 * Cinco fotos daban cinco toasts de «subido» y cinco de «agregada a la galería»: diez
 * interrupciones para un solo acto. Se cuenta el resultado una vez, al final.
 *
 * ── LO PARCIAL SE TERMINA Y SE DICE ─────────────────────────────────────────
 *
 * Un archivo que falla no corta el lote: los otros ya se eligieron y no hay motivo para
 * tirarlos. Se sube todo lo que pueda subirse y el aviso dice cuántos entraron y cuántos no —
 * callar la diferencia dejaría creyendo que están las cinco cuando hay cuatro.
 */
export function useUploadDocumentsMutation(
  ownerType: DocumentOwnerType,
  ownerId: string,
  options?: {
    /** Si viaja, cada archivo entra además a ese slot. La galería lo usa para no dejar la
     * foto suelta en el cartapacio: subir y después tener que arrastrarla sería el mismo
     * trabajo en dos pasos, y el segundo se olvida. */
    slot?: 'gallery'
    /**
     * Se llama en cada transición de CADA archivo: cuando le toca el turno, cuando entra y
     * cuando falla.
     *
     * Existe porque el lote es secuencial —el orden de la galería sale del orden en que las
     * llamadas terminan— y veinte archivos son varios minutos. Sin esto, ese rato era un spinner
     * en el que no se sabía si iba por el segundo o por el decimoctavo, ni cuál de los veinte
     * había fallado; y un lote parcial sin decir cuál falló obliga a comparar la lista a mano, o
     * a volver a subir «por las dudas» y duplicar lo que sí entró.
     *
     * Es un callback y no estado del hook a propósito: quien dibuja la cola es el diálogo, y
     * meterle estado de render a una mutación la ataría a UNA pantalla.
     */
    onItemChange?: (
      key: string,
      patch: { status: string; error?: string; unpublished?: boolean }
    ) => void
  }
) {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  const slot = options?.slot

  return useMutation({
    mutationFn: async (input: UploadDocumentsInput) => {
      const uploaded: string[] = []
      const failures: unknown[] = []
      /** Lo que se subió bien pero NO se pudo publicar. Se cuenta aparte: el archivo entró. */
      let unpublished = 0

      for (const [index, file] of input.files.entries()) {
        const key = uploadItemKey(file)
        options?.onItemChange?.(key, { status: UPLOAD_ITEM_STATUS.uploading })
        try {
          const created = await documentsAdminV2Service.upload(accessToken, {
            file,
            ownerType,
            ownerId,
            // El nombre visible es de UN archivo: con varios, cada uno se queda con el suyo.
            // Aplicarlo a todos dejaría cinco filas llamadas igual, que es peor que el nombre
            // del archivo — el diálogo por eso esconde el campo cuando hay más de uno.
            ...(input.files.length === 1 && input.name ? { name: input.name } : {}),
            ...(input.comment ? { comment: input.comment } : {}),
            ...(input.categoryId ? { categoryId: input.categoryId } : {})
          })

          if (slot) await documentsAdminV2Service.setSlot(accessToken, created.id, slot)

          if (input.publish) {
            // En su propio `try`: publicar puede fallar por permiso mientras subir salió bien, y
            // ahí el archivo YA está. Contarlo como fallo de subida haría creer que hay que
            // volver a subirlo, y volver a subirlo lo duplicaría.
            try {
              await documentsAdminV2Service.setVisibility(accessToken, created.id, 'public')
            } catch {
              unpublished += 1
              // «Subido, sin publicar» y no un fallo: el archivo ESTÁ. Marcarlo como fallo haría
              // creer que hay que volver a subirlo, y volver a subirlo lo duplicaría.
              options?.onItemChange?.(key, {
                status: UPLOAD_ITEM_STATUS.done,
                unpublished: true
              })
            }
          }

          uploaded.push(created.id)
          options?.onItemChange?.(key, { status: UPLOAD_ITEM_STATUS.done })
        } catch (error) {
          // Se guarda el índice junto al error: con cinco archivos, «falló uno» sin decir
          // cuál obliga a comparar la lista a mano.
          failures.push({ index, name: file.name, error })
          // Y el mensaje del servidor va a la FILA de su archivo, que es donde se puede leer: un
          // toast dice «falló uno» y se va antes de decir cuál.
          options?.onItemChange?.(key, {
            status: UPLOAD_ITEM_STATUS.failed,
            error: getErrorMessage(error)
          })
        }
      }

      // Todo falló: es un fracaso, no un éxito parcial, y tiene que entrar por la rama de
      // error para que el diálogo NO se cierre y no se pierda la selección.
      if (uploaded.length === 0 && failures.length > 0) {
        throw (failures[0] as { error: unknown }).error
      }

      return { uploaded, failed: failures.length, unpublished, published: Boolean(input.publish) }
    },
    onSuccess: ({ uploaded, failed, unpublished, published }) => {
      // Se nombra el estado en que quedó, no sólo el hecho: "subido" a secas dejaría
      // creyendo que el cliente ya lo ve, y publicar es otro acto.
      // El estado en que quedó, no sólo el hecho: «subido» a secas deja creyendo que el cliente
      // ya lo ve. Con la casilla marcada la frase se invierte —ya salió— porque es igual de
      // importante saber que algo LLEGÓ al teléfono como saber que no.
      const estado = published
        ? unpublished > 0
          ? ` ${unpublished} no se pudieron publicar: hazlo desde la lista.`
          : ' Ya son visibles para los clientes.'
        : slot
          ? ' Quedan internas hasta que las publiques.'
          : ' Quedan internas hasta que las publiques.'

      const uno = published
        ? unpublished > 0
          ? 'Se subió, pero no se pudo publicar: hazlo desde la lista.'
          : 'Subido y publicado. Ya es visible para los clientes.'
        : slot
          ? 'Foto agregada a la galería. Queda interna hasta que la publiques.'
          : 'Documento subido. Queda interno hasta que lo publiques.'

      if (failed > 0) {
        toast.warning(
          `Se subieron ${uploaded.length} de ${uploaded.length + failed}.${estado} Los demás fallaron: vuelve a intentarlo.`
        )
      } else if (uploaded.length === 1) {
        toast.success(uno)
      } else if (published && unpublished > 0) {
        toast.warning(`Se subieron ${uploaded.length}.${estado}`)
      } else {
        toast.success(`Se subieron ${uploaded.length}.${estado}`)
      }

      void queryClient.invalidateQueries({
        queryKey: queryKeys.documents.byOwner(ownerType, ownerId)
      })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export function useRemoveDocumentMutation(ownerType: DocumentOwnerType, ownerId: string) {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()

  return useMutation({
    mutationFn: async (id: string) => await documentsAdminV2Service.remove(accessToken, id),
    onSuccess: () => {
      toast.success('Documento eliminado')
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documents.byOwner(ownerType, ownerId)
      })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export function useSetDocumentVisibilityMutation(ownerType: DocumentOwnerType, ownerId: string) {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()

  return useMutation({
    mutationFn: async ({ id, visibility }: { id: string; visibility: DocumentVisibility }) =>
      await documentsAdminV2Service.setVisibility(accessToken, id, visibility),
    onSuccess: (result) => {
      toast.success(
        result.visibility === 'public'
          ? 'Documento publicado. Ya es visible para los clientes.'
          : 'Documento despublicado. Los clientes dejaron de verlo.'
      )
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documents.byOwner(ownerType, ownerId)
      })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

/**
 * Mete o saca una foto del slot `gallery`.
 *
 * NO publica: la foto sigue interna hasta que alguien la publique aparte. El toast lo dice
 * porque «agregada a la galería» se lee como «el cliente ya la ve», y no es cierto.
 */
export function useSetDocumentSlotMutation(ownerType: DocumentOwnerType, ownerId: string) {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()

  return useMutation({
    mutationFn: async (input: { id: string; slot: 'gallery' | null }) =>
      await documentsAdminV2Service.setSlot(accessToken, input.id, input.slot),
    onSuccess: (_data, input) => {
      toast.success(
        input.slot
          ? 'Agregada a la galería. Queda interna hasta que la publiques.'
          : 'Quitada de la galería. El archivo sigue en el cartapacio.'
      )
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documents.byOwner(ownerType, ownerId)
      })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

/**
 * Reescribe el orden de la galería entera.
 *
 * Sin toast de éxito a propósito: el resultado del arrastre YA se ve en la pantalla, y un
 * aviso por cada movimiento convierte acomodar diez fotos en diez interrupciones. El
 * error sí avisa, porque ahí la pantalla y el servidor dejaron de coincidir.
 */
export function useReorderDocumentSlotMutation(ownerType: DocumentOwnerType, ownerId: string) {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()

  return useMutation({
    mutationFn: async (attachmentIds: string[]) =>
      await documentsAdminV2Service.reorderSlot(accessToken, {
        ownerType,
        ownerId,
        slot: 'gallery',
        attachmentIds
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documents.byOwner(ownerType, ownerId)
      })
    },
    onError: (error, _vars, _ctx) => {
      toast.error(getErrorMessage(error))
      // Se refresca ANTE EL ERROR para que la grilla vuelva al orden real del servidor: si
      // se quedara con el optimista, la pantalla mostraría un orden que no existe.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documents.byOwner(ownerType, ownerId)
      })
    }
  })
}

/**
 * De dónde puede copiar su galería este dueño.
 *
 * `enabled` porque la lista se pide al ABRIR el diálogo y no antes: son los hermanos de un
 * proyecto con sus conteos, y cargarla con la pestaña dejaría a todo el mundo pagando una
 * agregación por una función que casi nadie abre en esa visita.
 */
export function useGallerySourcesQuery(
  ownerType: DocumentOwnerType,
  ownerId: string,
  enabled = true
) {
  const accessToken = useV2AccessToken()

  return useQuery({
    queryKey: queryKeys.documents.gallerySources(ownerType, ownerId),
    queryFn: async () =>
      await documentsAdminV2Service.listGallerySources(accessToken, ownerType, ownerId),
    enabled: enabled && Boolean(accessToken) && Boolean(ownerId),
    refetchOnWindowFocus: false,
    // Un 400 —este dueño no tiene galería— y un 403 son deterministas.
    retry: false
  })
}

/**
 * Copia la galería de otro dueño a éste.
 *
 * ── EL AVISO CUENTA LAS CUATRO COSAS QUE PASARON ────────────────────────────
 *
 * Copiadas, reusadas, desalojadas y las que no entraron por el tope. «Se copiaron 15» cuando
 * entraron 12 es la clase de mentira que se descubre en el teléfono de un cliente, y «listo» a
 * secas deja sin saber si el `replace` sacó algo que había costado acomodar.
 *
 * Invalida el cartapacio del DESTINO —las copias son filas nuevas de ese dueño— y su lista de
 * candidatos, porque ahora tiene fotos que antes no tenía. El origen no se toca: copiar no lo
 * modifica, que es justamente el punto.
 */
export function useCopyGalleryMutation(ownerType: DocumentOwnerType, ownerId: string) {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()

  return useMutation({
    mutationFn: async (input: {
      from: { ownerType: DocumentOwnerType; ownerId: string }
      mode: CopyGalleryMode
    }) =>
      await documentsAdminV2Service.copyGallery(accessToken, {
        from: input.from,
        to: { ownerType, ownerId },
        mode: input.mode
      }),
    onSuccess: (result) => {
      toast.success(copyGalleryNotice(result))
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documents.byOwner(ownerType, ownerId)
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documents.gallerySources(ownerType, ownerId)
      })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
